import { Search, X } from "lucide-react";
import { Input } from "./ui/input";
import { cn } from "@/lib/utils";
import type { Filters, Priority, Product, Status, Team } from "@/lib/types";

interface Props {
  teams: Team[];
  products: Product[];
  filters: Filters;
  setFilters: (f: Filters) => void;
}

const STATUSES: Status[] = ["planned", "in_progress", "done", "cancelled"];
const PRIORITIES: Priority[] = ["P0", "P1", "P2"];
const STATUS_LABEL: Record<Status, string> = {
  planned: "Planned",
  in_progress: "In progress",
  done: "Done",
  cancelled: "Cancelled",
};

export function FilterBar({ teams, products, filters, setFilters }: Props) {
  const toggle = <T,>(set: Set<T>, v: T): Set<T> => {
    const next = new Set(set);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    return next;
  };

  const hasAny =
    filters.teams.size > 0 ||
    filters.products.size > 0 ||
    filters.statuses.size > 0 ||
    filters.priorities.size > 0 ||
    filters.search.length > 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search initiatives, owners, Jira keys…"
            className="pl-8"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
        {hasAny && (
          <button
            onClick={() =>
              setFilters({
                teams: new Set(),
                products: new Set(),
                statuses: new Set(),
                priorities: new Set(),
                search: "",
              })
            }
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      <FilterGroup label="Teams">
        {teams.map((t) => (
          <FilterChip
            key={t.id}
            active={filters.teams.has(t.id)}
            color={t.color}
            onClick={() => setFilters({ ...filters, teams: toggle(filters.teams, t.id) })}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: t.color }}
            />
            {t.name}
          </FilterChip>
        ))}
      </FilterGroup>

      <FilterGroup label="Products">
        {products.map((p) => (
          <FilterChip
            key={p.id}
            active={filters.products.has(p.id)}
            onClick={() =>
              setFilters({ ...filters, products: toggle(filters.products, p.id) })
            }
          >
            {p.name}
          </FilterChip>
        ))}
      </FilterGroup>

      <FilterGroup label="Status">
        {STATUSES.map((s) => (
          <FilterChip
            key={s}
            active={filters.statuses.has(s)}
            onClick={() => setFilters({ ...filters, statuses: toggle(filters.statuses, s) })}
          >
            {STATUS_LABEL[s]}
          </FilterChip>
        ))}
      </FilterGroup>

      <FilterGroup label="Priority">
        {PRIORITIES.map((p) => (
          <FilterChip
            key={p}
            active={filters.priorities.has(p)}
            onClick={() => setFilters({ ...filters, priorities: toggle(filters.priorities, p) })}
          >
            {p}
          </FilterChip>
        ))}
      </FilterGroup>
    </div>
  );
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-xs font-medium text-muted-foreground w-16 pt-1.5 shrink-0 uppercase tracking-wide">
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function FilterChip({
  active,
  color,
  onClick,
  children,
}: {
  active: boolean;
  color?: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors",
        active
          ? "bg-primary/15 border-primary/40 text-foreground"
          : "bg-secondary/40 border-border text-muted-foreground hover:bg-secondary",
      )}
      style={active && color ? { borderColor: `${color}80` } : undefined}
    >
      {children}
    </button>
  );
}
